import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Box, Button, Paper, CircularProgress } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CourseQuizQuestion from "./CourseQuizQuestion";
import ResponseTracker from "./ResponseTracker";
import SubmissionConfirmation from "./SubmissionConfirmation";
import CourseQuizInstructions from "./CourseQuizInstructions";
import { useTranslation } from "react-i18next";
import ButtonGroup from "../Buttons/ButtonGroup";
import { serialize } from "../../redux/utils/jsona";
import { questionTypes, resourceObjectTypes } from "../../constants";
import { isEmpty, every } from "lodash";
import Hidden from "../Hidden/Hidden";
import { makeGetEntities } from "../../redux/selectors/selectors";
import { useSelector } from "react-redux";
import QuizCompletionModal from "./QuizCompletionModal";
import useLoadingState from "../../hooks/useLoadingState";
import CourseQuizNavigationButtons from "./CourseQuizNavigationButtons";

const allQuestionsPerPage = "all";
const selector = makeGetEntities();

const quizViews = {
  instructionsView: "instructions",
  questionsView: "questions",
  submissionConfirmationView: "confirmation"
};

const useStyles = makeStyles(theme => ({
  quizCard: {
    padding: theme.spacing(7),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4)
    }
  },
  cardContent: {
    flexDirection: "column",
    margin: theme.spacing(1),
    paddingBottom: theme.spacing(0),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1, 1, 0, 1)
    }
  },
  arrowLeft: {
    transform: "rotate(180deg)"
  }
}));

const getVisibleQuestions = (allQuestions, currentPage, questionsPerPage) => {
  if (!allQuestions) {
    return [];
  } else if (questionsPerPage === allQuestionsPerPage || !questionsPerPage) {
    return allQuestions;
  } else {
    const numPerPage = parseInt(questionsPerPage);
    return allQuestions.slice(
      currentPage * numPerPage,
      currentPage * numPerPage + numPerPage
    );
  }
};

const getQuizType = (t, survey) => {
  return survey
    ? t("contentViewer.contentViewerHeader.survey")
    : t("contentViewer.contentViewerHeader.quiz");
};

const isQuestionAnswered = (question, questionResponse) => {
  if (!questionResponse) {
    return false;
  }
  if (question.questionType === questionTypes.matching) {
    return questionResponse.length === question.matchClues.length;
  } else if (question.questionType === questionTypes.ordering) {
    return every(questionResponse, ({ order }) => Number.isInteger(order));
  } else if (
    question.questionType === questionTypes.shortAnswer ||
    question.questionType === questionTypes.longAnswer
  ) {
    return (
      !!questionResponse.answerText &&
      typeof questionResponse.answerText === "string"
    );
  } else if (question.questionType === questionTypes.fillBlanks) {
    const numBlanks = question.fillInTheBlanks.filter(
      ({ type }) => type === resourceObjectTypes.fillInTheBlankAnswerType
    ).length;
    return (
      questionResponse &&
      questionResponse.filter(response => !!response.answerText).length ===
        numBlanks
    );
  } else {
    return !isEmpty(questionResponse);
  }
};

const getConfirmationQuestionsList = (questions, answers, mandatory) => {
  const unansweredQuestions = [];
  questions.forEach((question, index) => {
    if (!isQuestionAnswered(question, answers[question.id])) {
      unansweredQuestions.push({
        questionNumber: index + 1,
        mandatory: question.mandatory
      });
    }
  });
  return unansweredQuestions;
};

const getAnsweredQuestions = (questions, answers) => {
  return questions
    ? questions.map(question => {
        const questionResponse = answers[question.id];
        return isQuestionAnswered(question, questionResponse);
      })
    : [];
};

const getCorrectQuestions = questions => {
  return questions
    ? questions.map(question => {
        return question.learnerResponse && question.learnerResponse.isCorrect;
      })
    : [];
};

const isResuming = questionsWithResponses =>
  questionsWithResponses && questionsWithResponses.find(question => question);

const getStartingView = quiz =>
  quiz && quiz.instructions
    ? quizViews.instructionsView
    : quizViews.questionsView;

const getQuestionNumber = (questionNumber, currentPage, questionsPerPage) =>
  currentPage *
    (!questionsPerPage || questionsPerPage === allQuestionsPerPage
      ? 0
      : parseInt(questionsPerPage)) +
  questionNumber +
  1;

const CourseQuizInstructionsActions = ({
  nextStep,
  resuming,
  submitted,
  quizType
}) => {
  const { t } = useTranslation();
  let label;
  if (submitted) {
    label = t("courseQuiz.CourseQuizInstructionsActions.revisit", {
      quizType: quizType
    });
  } else if (resuming) {
    label = t("courseQuiz.CourseQuizInstructionsActions.resume", {
      quizType: quizType
    });
  } else {
    label = t("courseQuiz.CourseQuizInstructionsActions.start", {
      quizType: quizType
    });
  }

  return (
    <CourseQuizCardActions>
      <Button
        color="primary"
        variant="contained"
        onClick={nextStep}
        data-qa="quiz-instructions-btn"
      >
        {label}
      </Button>
    </CourseQuizCardActions>
  );
};

const CourseQuizConfirmationActions = ({
  prevStep,
  onQuizSubmit,
  quizType,
  submitted,
  isSubmitting,
  submitBtnDisabled
}) => {
  const { t } = useTranslation();
  return (
    <CourseQuizCardActions>
      <Button color="primary" onClick={prevStep} disabled={isSubmitting}>
        {t("contentViewer.navigationButtons.back")}
      </Button>
      {!submitted && (
        <Button
          color="primary"
          variant="contained"
          onClick={() => {
            onQuizSubmit();
          }}
          startIcon={
            isSubmitting ? <CircularProgress color="inherit" size={18} /> : null
          }
          disabled={submitBtnDisabled}
        >
          {t("contentViewer.navigationButtons.submit")} {quizType}
        </Button>
      )}
    </CourseQuizCardActions>
  );
};

const CourseQuizConfirmationContent = ({
  prevStep,
  unansweredQuestions,
  quizType
}) => {
  return (
    <SubmissionConfirmation
      prevStep={prevStep}
      unansweredQuestions={unansweredQuestions}
      quizType={quizType}
    />
  );
};

const CourseQuizCardActions = ({ children }) => {
  return (
    <Box display="flex" justifyContent="flex-end">
      <ButtonGroup align="right">{children}</ButtonGroup>
    </Box>
  );
};

const CourseQuizCard = ({ content, actions }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.quizCard}>
      {content}
      {actions}
    </Paper>
  );
};

// TODO: Break this up into smaller components
const CourseQuiz = ({
  contentableId,
  questions,
  survey,
  quiz,
  submitted,
  navigationButtons,
  onSubmitQuiz,
  learnerAnswers,
  showModal,
  userAssignmentId,
  updateQuizAnswer,
  isSubmitting,
  quizCompletionModalOpen,
  onCloseQuizCompletionModal,
  quizScore,
  onNextActivity,
  onExitShortCourse,
  lastActivity,
  moduleCompleted,
  questionsPerPage,
  skeletonLoader
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState(getStartingView(quiz));
  const [questionResponses, setQuestionResponses] = useState(learnerAnswers);
  const [currentQuestionsPage, setCurrentQuestionsPage] = useState(0);
  const isFetchingQuiz = useLoadingState("FETCH_CONTENTABLE");
  const quizContentable = useSelector(({ learningCenter: { contentable } }) =>
    selector(contentable, {
      type: resourceObjectTypes.quizContentable,
      id: contentableId
    })
  );
  const quizType = getQuizType(t, survey);
  const numQuizQuestions = questions ? questions.length : 0;
  const visibleQuestions = getVisibleQuestions(
    questions,
    currentQuestionsPage,
    questionsPerPage
  );
  const handleChangeQuestionsPage = newPage => {
    setCurrentQuestionsPage(newPage);
  };

  // Proceed to next step
  const nextStep = () => {
    let nextView = "";
    if (view === "instructions") {
      nextView = "questions";
    } else if (view === "questions") {
      nextView = "confirmation";
    }
    setView(nextView);
  };

  // Go back to prev step
  const prevStep = () => {
    let prevView = "";
    if (view === "questions") {
      if (submitted) {
        prevView = "instructions";
      } else {
        showModal && showModal();
        return;
      }
    } else if (view === "confirmation") {
      prevView = "questions";
    }
    setView(prevView);
  };

  // TODO: this needs to be completely refactored
  const handleAnswerChange = (questionType, questionResponse, questionId) => {
    let currentResponses = { ...questionResponses };
    let needsDelete = false;
    let serialized = false;
    if (questionType === "multiple_select") {
      let response = currentResponses[questionId] || [];
      const idx = response.findIndex(
        elem => elem.answerData.id === questionResponse.id
      );
      if (idx >= 0) {
        needsDelete = true;
        response = response.filter(
          elem => elem.answerData.id !== questionResponse.id
        );
      } else {
        response.push({
          answerData: questionResponse,
          questionId: questionId
        });
      }
      currentResponses[questionId] = response;
    } else if (questionType === questionTypes.fillBlanks) {
      let response = [...(currentResponses[questionId] || [])];
      let currentBlank = response.find(blank =>
        blank ? `${blank.blankId}` === `${questionResponse.blankId}` : false
      );
      if (!currentBlank) {
        response.push({
          blankId: questionResponse.blankId,
          answerText: questionResponse.answer_text
        });
      } else {
        currentBlank.answerText = questionResponse.answer_text;
      }
      currentResponses[questionId] = response;
    } else if (questionType === questionTypes.matching) {
      currentResponses[questionId] = currentResponses[questionId] || [];
      let response = [
        ...(currentResponses && currentResponses.hasOwnProperty(questionId)
          ? currentResponses[questionId]
          : "")
      ];
      let match = response.find(
        match => `${match.matchClue.id}` === `${questionResponse.matchClueId}`
      );
      if (!match) {
        currentResponses[questionId].push({
          matchChoice: { id: questionResponse.matchChoiceId },
          matchClue: { id: questionResponse.matchClueId }
        });
      } else {
        match.matchChoice.id = questionResponse.matchChoiceId;
      }
    } else if (
      questionType === questionTypes.shortAnswer ||
      questionType === questionTypes.longAnswer
    ) {
      currentResponses[questionId] = {
        answerText: questionResponse.answer_text
      };
    } else if (questionType === questionTypes.ordering) {
      currentResponses[questionId] = questionResponse;
    } else {
      currentResponses[questionId] = {
        answerData: questionResponse,
        questionId: questionId
      };
    }

    // The user assignment ID is needed in order for the update answer action to
    // work, otherwise it'll return a 500 internal server error.
    if (questionType === "ordering" && Array.isArray(questionResponse)) {
      const questionResponseArray = [...questionResponse];
      const paramsForSerialization = {
        type: "learnerResponse",
        question_id: questionId,
        user_assignment_id: userAssignmentId,
        relationshipNames: ["answers"],
        answers: questionResponseArray
      };
      questionResponse = serialize(paramsForSerialization, ["answers"]);
      serialized = true;
    } else {
      questionResponse.user_assignment_id = userAssignmentId;
    }
    setQuestionResponses(currentResponses);
    updateQuizAnswer &&
      updateQuizAnswer(
        questionId,
        { ...questionResponse, questionId },
        needsDelete,
        serialized
      );
  };

  const getCurrentQuestionResponse = questionId => {
    const currentQuestion =
      questions && questions.find(({ id }) => id === questionId);
    let response = questionResponses[questionId];
    if (
      currentQuestion &&
      (currentQuestion.questionType === questionTypes.fillBlanks ||
        currentQuestion.questionType === questionTypes.matching ||
        currentQuestion.questionType === questionTypes.ordering)
    ) {
      return response;
    } else if (currentQuestion.questionType === questionTypes.ordering) {
      return (
        response &&
        response.map(response =>
          response.answerData ? response.answerData : response
        )
      );
    } else if (Array.isArray(response)) {
      return response.map(resp => resp.answerData.id);
    } else if (response && response.answerData) {
      return [response.answerData.id];
    } else if (response && response.answerText) {
      return response.answerText;
    }
    return [];
  };

  const handleCloseQuizCompletionModal = () => {
    setView(quizViews.questionsView);
    onCloseQuizCompletionModal();
  };

  const answeredQuestions =
    submitted && !survey
      ? getCorrectQuestions(quizContentable.questions)
      : getAnsweredQuestions(questions, questionResponses);

  const unansweredQuestions = getConfirmationQuestionsList(
    questions,
    questionResponses
  );

  useEffect(() => {
    setQuestionResponses(learnerAnswers);
    setView(getStartingView(quiz));
    setCurrentQuestionsPage(0);
  }, [quiz]);

  useEffect(() => {
    if (submitted) {
      setCurrentQuestionsPage(0);
    }
  }, [submitted]);

  const resuming = isResuming(answeredQuestions);
  const hasUnansweredMandatoryQuestions =
    unansweredQuestions.filter(question => question.mandatory).length > 0;

  return (
    <>
      {isFetchingQuiz ? (
        skeletonLoader
      ) : (
        <>
          <Box mb={5} width="100%">
            <Hidden
              hidden={view !== quizViews.instructionsView}
              data-qa="quiz-instructions"
            >
              <CourseQuizCard
                content={
                  <CourseQuizInstructions instructions={quiz.instructions} />
                }
                actions={
                  <CourseQuizInstructionsActions
                    nextStep={nextStep}
                    resuming={resuming}
                    submitted={submitted}
                    quizType={quizType}
                  />
                }
              />
            </Hidden>
            <Hidden hidden={view !== quizViews.submissionConfirmationView}>
              <CourseQuizCard
                content={
                  <CourseQuizConfirmationContent
                    prevStep={prevStep}
                    unansweredQuestions={unansweredQuestions}
                    quizType={quizType}
                  />
                }
                actions={
                  <CourseQuizConfirmationActions
                    prevStep={prevStep}
                    onQuizSubmit={onSubmitQuiz}
                    quizType={quizType}
                    submitted={submitted}
                    isSubmitting={isSubmitting}
                    submitBtnDisabled={hasUnansweredMandatoryQuestions}
                  />
                }
              />
            </Hidden>
            <Hidden hidden={view !== quizViews.questionsView}>
              {visibleQuestions.map((question, index) => (
                <CourseQuizQuestion
                  text={question.text}
                  questionType={question.questionType}
                  answerChoices={question.answerChoices}
                  matchChoices={question.matchChoices}
                  matchClues={question.matchClues}
                  points={question.points}
                  allowHtml={question.allowHtml}
                  fillInTheBlanks={question.fillInTheBlanks}
                  key={index}
                  questionNumber={getQuestionNumber(
                    index,
                    currentQuestionsPage,
                    questionsPerPage
                  )}
                  questionId={question.id}
                  currentAnswer={getCurrentQuestionResponse(question.id)}
                  submitted={submitted}
                  onAnswerChange={handleAnswerChange}
                  isSurvey={survey}
                  learnerAnswer={learnerAnswers[question.id]}
                />
              ))}
            </Hidden>
          </Box>
          {view === quizViews.questionsView && (
            <ResponseTracker
              answeredQuestions={answeredQuestions}
              showCorrectAnswers={submitted && !survey}
              navigationButtons={navigationButtons}
              quizActionButtons={
                <CourseQuizNavigationButtons
                  quizType={quizType}
                  onSubmitQuiz={nextStep}
                  quizSubmitted={submitted}
                  numQuestions={numQuizQuestions}
                  questionsPerPage={questionsPerPage}
                  currentPage={currentQuestionsPage}
                  onChangePage={handleChangeQuestionsPage}
                />
              }
            />
          )}
        </>
      )}
      <QuizCompletionModal
        open={quizCompletionModalOpen}
        onClose={handleCloseQuizCompletionModal}
        score={quizScore}
        onNextActivityClick={onNextActivity}
        onReviewQuizClick={handleCloseQuizCompletionModal}
        onExitShortCourse={onExitShortCourse}
        lastActivity={lastActivity}
        moduleCompleted={moduleCompleted}
      />
    </>
  );
};

CourseQuiz.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The question ID
       */
      id: PropTypes.number,
      /**
       * The question number
       */
      order: PropTypes.number,
      /**
       * The question text
       */
      text: PropTypes.string,
      /**
       * The amount of points the question is worth
       */
      points: PropTypes.number,
      answerChoices: PropTypes.arrayOf(
        PropTypes.shape({
          /**
           * The answer text
           */
          text: PropTypes.string
        })
      ),
      matchClues: PropTypes.arrayOf(
        PropTypes.shape({
          /**
           * The match clue text
           */
          text: PropTypes.string,
          position: PropTypes.number
        })
      ),
      matchChoices: PropTypes.arrayOf(
        PropTypes.shape({
          /**
           * The match clue text
           */
          text: PropTypes.string,
          position: PropTypes.number
        })
      )
    })
  ),
  learnerAnswers: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.number,
        /**
         * The question id
         */
        questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        /**
         * The answer choice id
         */
        answerData: PropTypes.any
      }),
      PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          /**
           * The question id
           */
          questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          /**
           * The answer choice id
           */
          answerData: PropTypes.any
        })
      )
    ])
  ),
  submitted: PropTypes.bool,
  showModal: PropTypes.func,
  userAssignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default CourseQuiz;
